import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { patchMe } from '../api/users'
import { fallbackLanguage, isSupportedLanguage, setAppLanguage, type SupportedLanguage } from '../i18n'
import type { UserResponse } from '../types/user'
import { useCurrentUser } from './useCurrentUser'
import { useTranslation } from './useTranslation'

export function useLanguagePreference() {
  const currentUser = useCurrentUser()
  const { language } = useTranslation()
  const queryClient = useQueryClient()
  const [isSavingLanguage, setIsSavingLanguage] = useState(false)

  useEffect(() => {
    if (!isSupportedLanguage(currentUser.preferredLanguage)) {
      return
    }

    void setAppLanguage(currentUser.preferredLanguage)
  }, [currentUser.preferredLanguage])

  const currentLanguage: SupportedLanguage = isSupportedLanguage(currentUser.preferredLanguage)
    ? currentUser.preferredLanguage
    : isSupportedLanguage(language)
      ? language
      : fallbackLanguage

  async function updateLanguage(nextLanguage: SupportedLanguage) {
    setIsSavingLanguage(true)

    try {
      const updatedUser = await patchMe({ preferredLanguage: nextLanguage })
      queryClient.setQueryData<UserResponse>(['me'], updatedUser)
      await setAppLanguage(updatedUser.preferredLanguage)
    } finally {
      setIsSavingLanguage(false)
    }
  }

  return {
    currentLanguage,
    isSavingLanguage,
    updateLanguage,
  }
}
